import React from 'react'
import { Link } from 'react-router-dom'
import { Home, Search, FileText, Image, Code } from 'lucide-react'
import { useSEO } from '../hooks/useSEO'

const NotFound: React.FC = () => {
  useSEO({
    title: '404 - Page Not Found | SnapTools',
    description: 'The page you are looking for could not be found. Explore our free PDF converter, image tools, SEO utilities, and developer tools.',
    keywords: 'page not found, 404, SnapTools, PDF tools, image converter, developer tools',
    canonical: 'https://snap-tools.online/404'
  })

  const popularTools = [
    {
      title: 'PDF Converter',
      description: 'Convert documents to PDF format',
      href: '/pdf/convert',
      icon: FileText
    },
    {
      title: 'Image Resizer',
      description: 'Resize images while maintaining quality',
      href: '/image/resize',
      icon: Image
    },
    {
      title: 'JSON Formatter',
      description: 'Format and validate JSON data',
      href: '/text/json-formatter',
      icon: Code
    },
    {
      title: 'UUID Generator',
      description: 'Generate unique identifiers',
      href: '/developer/uuid-generator',
      icon: Code
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#8438ff] to-[#5d2ca8] flex items-center justify-center px-6">
      <div className="max-w-4xl mx-auto text-center">
        <div className="mb-8">
          <h1 className="text-8xl md:text-9xl font-bold text-white/20 mb-4">404</h1>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Oops! Page Not Found
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            The page you're looking for doesn't exist. But don't worry, we have plenty of useful tools waiting for you!
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Link 
            to="/" 
            className="inline-flex items-center px-6 py-3 bg-white text-[#8438ff] rounded-lg hover:bg-gray-100 transition-colors font-semibold"
          >
            <Home className="mr-2 h-5 w-5" />
            Back to Home
          </Link>
          <Link 
            to="/pdf/convert" 
            className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white rounded-lg hover:bg-white hover:text-[#8438ff] transition-colors font-semibold"
          >
            <Search className="mr-2 h-5 w-5" />
            Explore Tools
          </Link>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-6">Popular Tools</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularTools.map((tool) => (
              <Link
                key={tool.title}
                to={tool.href}
                className="bg-white/10 rounded-lg p-6 hover:bg-white/20 transition-colors group"
              >
                <tool.icon className="h-8 w-8 text-white mb-3 group-hover:scale-110 transition-transform" />
                <h4 className="text-lg font-semibold text-white mb-2">{tool.title}</h4>
                <p className="text-white/80 text-sm">{tool.description}</p>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 text-white/70">
          <p>
            Need help? Visit our <Link to="/" className="text-white hover:underline">homepage</Link> or 
            try searching for the tool you need.
          </p>
        </div>
      </div>
    </div>
  )
}

export default NotFound
